export default {
  en: {
    title: "Agent builder",
    subtitle: "Build agents step by step, try them and release them.",
    tab: { graph: "Steps", prompts: "Saved prompts", runner: "Test runner", docs: "For developers", flow: "How it ran" },
    tabHelp: { graph: "Draw and change the agent's steps, and say what it may use.", prompts: "Instructions for the AI kept in a library, so several steps or agents can share them.", try: "Ask the agent something and watch each step work, without affecting real systems.", tests: "Saved questions with the answers you expect, run again before each release.", triggers: "When the agent starts on its own: on a schedule or when another system calls it.", flow: "How real requests went through the steps: where they waited, failed or finished.", docs: "For developers who write agents as files: command-line tools and file reference." },
    tier: "Level {{tier}}",
    valid: "No problems found",
    issues: "{{n}} issue(s)",
    issuesTitle: "Validation issues",
    spec: { "invoice-assistant": "Example: invoice assistant", react: "Example: think, use a system, repeat", router: "Example: sort the request, then answer it the right way", plan_execute: "Example: plan first, then do each part", reflection: "Example: write, check, improve", supervisor: "Example: a supervisor asks a team of agents" },
    meta: { name: "Name", description: "Description", pattern: "How its steps are arranged", patternHelp: "A short description of the agent's shape, shown in lists. It doesn't change what the agent does: the steps on the graph do.", risk: "Risk level", validation: "Checks" },
    pattern: { react: "Think, use a system, repeat", plan_execute: "Plan first, then do each part", reflection: "Write, check, improve", router: "Sort the request, then answer it the right way", supervisor: "A supervisor asks other agents and combines their answers", handoff: "One agent helps at a time and can hand the conversation on", debate: "Several agents argue, one decides", hierarchical: "Teams of agents under a lead", custom: "Its own steps" },
    riskHelp: { 1: "Level 1: may use only the lowest-risk actions, mostly reading and answering.", 2: "Level 2: may also use actions that change data in limited ways. The highest level for agents made in the guided builder.", 3: "Level 3: may change important data. Its changes wait for a person's approval (unless your administrator set otherwise), it always works for a person, and going live needs two approvers.", 4: "Level 4: the most sensitive actions, with the same safeguards as level 3." },
    node: { llm: "Model", tool: "Tool", router: "Router", human_approval: "Human approval", human_input: "Human input", parallel: "Parallel", join: "Join", subgraph: "Sub-agent", retrieval: "Retrieval", python: "Python", decision: "Decision", end: "Finish", wait: "Wait for", calculate: "Calculate", foreach: "For each item", table: "Table" },
    graph: {
      select: "Agent to open",
      figure: "Graph of agent {{name}}",
      loop: "Loop ≤ {{n}}",
      entry: "Entry",
      json: "The agent file (for developers)",
      controls: "Graph controls",
      zoomIn: "Zoom in",
      zoomOut: "Zoom out",
      fit: "Fit to view",
      interactive: "Toggle interactivity",
      nodeHelp: "Graph node",
      edgeHelp: "Graph edge",
    },
    about: {
      versionPlain: "Shown to people as version {{version}}.",
      ownerHelp: "The team or person responsible for this agent. Shown in the agent list.",
      constraints: "Rules it must always follow",
      constraintsHelp: "Given to the AI at every step, with its role and goals. For example: “Never share an employee's salary.”",
      constraintN: "Rule {{n}}",
      constraintAr: "Rule {{n}} (Arabic)",
      constraintEn: "Rule {{n}} (English)",
      removeConstraint: "Remove rule {{n}}",
      addConstraint: "Add a rule",
      noConstraints: "No rules yet.",
      temperature: "Creativity (0 exact, 1 creative)",
      temperatureHelp: "Lower gives the same answer each time; higher gives more varied wording. Steps can set their own.",
      answerTokens: "Longest answer (tokens)",
      answerTokensHelp: "How long one AI answer may be. About 750 English words per 1,000 tokens. Steps can set their own.",
      topK: "Passages passed on from {{collection}}",
      topKHelp: "How many of the best-matching passages a search step passes on (1 to 50). More gives the AI more to read, but costs more.",
      approvalRule: "Before “{{action}}” in {{tool}}",
      approvalWho: "Who approves",
      approvalWithin: "Decide within",
      approvalWithinHelp: "How long the request waits in the approvers' inbox.",
      approvalEscalate: "If nobody decides in time, send it to",
      approvalEscalateHelp: "Half-way through the time a reminder is sent; when the time is up the request goes to the next of these roles, in order, each with the same time again. Empty: it stays with the approvers and is marked late.",
      approvalEscalateNone: "Nobody else (it stays and is marked late)",
      removeRule: "Remove this approval",
      addRule: "Ask for approval before…",
      addRuleHelp: "Pick an action that changes data. A person must approve before the agent does it.",
      rulesHelp: "These add to the approvals your organisation always asks for; they never remove one.",
      groups: "Only these groups may use it",
      groupsHelp: "Leave empty so everyone who may use agents can use it. Type a group's name and press Enter.",
      maxSteps: "Steps per request",
      maxStepsHelp: "A request stops after this many steps.",
      timeLimit: "Time limit (minutes)",
      timeLimitHelp: "A request stops when it takes longer.",
      maxTokens: "AI text per request (tokens)",
      maxTokensHelp: "How much text the AI may read and write in one request.",
      autonomousGuided: "Lets its schedule or event start it without a person asking. It stays a guided agent; changes it makes are checked as its approval rules say.",
      name: "Technical name",
      nameHelp: "Lowercase letters, digits and hyphens",
      version: "Version",
      description_ar: "What it does (Arabic)",
      description_en: "What it does (English)",
      role_ar: "Its role (Arabic)",
      role_en: "Its role (English)",
      model: "AI model",
      knowledge: "Knowledge it searches",
      knowledgeHelp: "Document collections this agent may read. Steps that search knowledge use these.",
      maxCost: "Spending limit per request (USD)",
      maxCostHelp: "A request stops when it would cost more than this.",
      autonomous: "Works on its own",
      autonomousHelp: "Lets its schedule or another system start it without a person asking. Turn on only after reviewing what it does; it then counts as a developer-reviewed agent and opens here, not in the guided builder.",
      autonomousTier: "High-risk agents (risk level 3 or 4) always work for a person.",
    },
    channel: { web: "Web", api: "API", widget: "Website widget", email: "Email", chat_app: "Chat app", chat: "Chat app", voice: "Voice", mcp: "Other AI tools (MCP)" },
    cert: { none: "Not certified", bronze: "Bronze", silver: "Silver", gold: "Gold" },
    source: { schema: "Schema", semantic: "Rule", nocode: "No-code" },
    pick: {
      versionN: "Version {{version}}",
      new: "New",
      newAgent: "New agent (blank)",
      examples: "Examples",
      registry: "Registered agents",
      version: "Version",
      state: { draft: "draft", submitted: "saved" },
      noVersions: "This agent has no versions yet.",
      registryUnavailable: "The agent registry is unavailable; only examples can be opened.",
      loadFailed: "Could not load the agent",
    },
    save: {
      saveAs: "Save as version {{version}}",
      saveAsHint: "Saves your changes as a version that can be released. A saved version never changes; later changes become the next version.",
      savedAs: "Saved as version {{version}}",
      savedHint: "This version is saved and can't change. Change anything to save the next version.",
      submittedAs: "Saved as version {{version}}",
      draftedAs: "Draft of version {{version}} saved",
      releaseDev: "Release version {{version}} to Development",
      releaseReason: "Released from the graph editor to try it",
      released: "Version {{version}} now runs in Development",
      releaseWaiting: "Version {{version}} is waiting to be released",
      releaseFailed: "The release did not work",
      draft: "Save draft",
      submit: "Save as version",
      drafted: "Draft saved: {{ref}}",
      submitted: "Version submitted: {{ref}}",
      immutableTitle: "This version is saved and can't change",
      bumpRequired: "Version {{version}} is already saved. Use the next version to save your changes.",
      rejected: "The registry rejected the spec",
      forbidden: "You need the builder or agent-admin role to save agents",
      failed: "Saving failed",
    },
    editor: {
      region: "Graph editor",
      palette: "Add a node",
      addNode: "Add {{type}} node",
      undo: "Undo",
      undoHint: "Ctrl+Z to undo, Ctrl+Shift+Z to redo",
      redo: "Redo",
      deleteSelected: "Delete selected",
      autoLayout: "Auto layout",
      unsaved: "Unsaved changes",
      canvasHelp: "Drag a step to move it. Drag from the dot on a step's edge to another step to add an arrow. Select a step or an arrow and press Delete to remove it. Everything can also be done from the panel on the side.",
      downloadHelp: "Saves the agent as a file (JSON) that developers can keep in code or load again.",
      nodeAria: "{{kind}} node {{label}}",
      edgeAria: "Edge from {{from}} to {{to}}",
      nodeHelp: "Press Enter or Space to select, arrow keys to move, Delete to remove.",
      edgeHelp: "Press Enter or Space to select, Delete to remove.",
      hasIssues: "has issues",
      showNode: "Show {{id}}",
      selection: "Selection",
      agent: "About this agent",
      download: "Download as a file",
      nothingSelected: "Select a step or an arrow on the canvas, or use + on an arrow to add a step.",
      setEntry: "Make entry node",
      isEntry: "Entry node",
      deleteNode: "Delete node",
      deleteEdge: "Delete edge",
      edge: "Edge",
      idHelp: "Lowercase letters, digits, - or _. Renaming updates every edge.",
      idInvalid: "Use lowercase letters, digits or _, starting with a letter.",
      idTaken: "Another node already has this id.",
      promptRefHelp: "Registry prompt, e.g. prompts:/invoice-system@production",
      llmToolsHelp: "Granted tools the model may call.",
      toolHelp: "Choosing a tool and action grants it in the spec.",
      pickTool: "Choose a tool",
      pickAction: "Choose an action",
      argsHelp: "JSON object; values may reference the run state (state.key).",
      argsInvalid: "Arguments must be a JSON object.",
      routeWhen: "Condition of route {{n}}",
      routeTo: "Target of route {{n}}",
      removeRoute: "Remove route {{n}}",
      addRoute: "Add route",
      slaHelp: "ISO 8601 duration, e.g. PT4H",
      branchesHelp: "At least two nodes run in parallel.",
      agentHelp: "Pick another registered agent and one of its submitted versions.",
      collectionHelp: "Choosing a collection declares it as agent knowledge.",
      entrypointHelp: "module.path:function",
      whenHelp: "Condition on the run state; empty means always.",
      outgoing: "Outgoing edges",
      noOutgoing: "No outgoing edges.",
      connectTo: "Connect to node",
      connect: "Connect",
      bump: "Use the next version",
      goals: "Goals",
      goalN: "Goal {{n}}",
      goalAr: "Goal {{n}} (Arabic)",
      goalEn: "Goal {{n}} (English)",
      removeGoal: "Remove goal {{n}}",
      addGoal: "Add goal",
      grants: "Granted tools",
      noGrants: "No systems allowed yet. Adding a “Use a system” step allows the system it uses.",
      field: {
        id: "Node id",
        label_ar: "Label (Arabic)",
        label_en: "Label (English)",
        promptRef: "Prompt reference",
        template: "Prompt template",
        variables: "Prompt variables",
        llmTools: "Tools the model may call",
        outputKey: "Output key",
        tool: "Tool",
        action: "Action",
        args: "Arguments",
        routes: "Routes",
        default: "Default route",
        subjectKey: "Subject key (state)",
        approverRoles: "Approver roles",
        sla: "Approval SLA",
        onReject: "On rejection go to",
        question_ar: "Question (Arabic)",
        question_en: "Question (English)",
        branches: "Branches",
        join: "Join node",
        strategy: "Join strategy",
        agent: "Sub-agent",
        collection: "Collection",
        query: "Query",
        entrypoint: "Entry point",
        maxIterations: "Maximum iterations",
        when: "Condition (when)",
        owner: "Owner",
        maxClassification: "Most sensitive data it may handle",
      },
      strategy: { merge: "Merge", list: "List", vote: "Vote" },
      classification: { public: "Public", internal: "Internal", confidential: "Confidential", restricted: "Restricted" },
      edgeKind: { edge: "edge", route: "route", default: "default", branch: "parallel", reject: "rejected", low_confidence: "not sure", limit: "if the limit is reached", error: "if it fails", give_up: "if nothing comes in time", each: "for each item" },
      edgeKindHelp: {
        edge: "Edge between two nodes.",
        route: "Router route.",
        default: "The router's default route; change it in the router's settings.",
        branch: "A parallel branch; change it in the parallel node's settings.",
        reject: "Where a rejected approval goes; change it in the approval node's settings.",
        low_confidence: "Where an unsure decision goes; change it in the decision node's settings.",
        limit: "Where the run goes once the step has repeated as often as allowed; change it in the step's limits.",
        error: "Where the run goes when the system still fails after its tries; change it under the step's “If it fails”.",
        give_up: "Where the run goes when nothing came in time; change it in the wait step.",
        each: "The first step done for each item of the list; change it in the for-each step.",
      },
    },
    plain: {
      lang: { ar: "Arabic", en: "English" },
      grants: "Allowed systems",
      grantsHelp: "What this agent may do in other systems. Steps can only use what is allowed here.",
      revoke: "Stop allowing",
      revokeAria: "Stop allowing {{tool}}: {{action}}",
      advanced: "Advanced settings",
      outgoing: "Next steps",
      toStep: "Then ‘{{step}}’",
      connection: "From ‘{{from}}’ to ‘{{to}}’",
      changesData: "changes data",
      route: "Way {{n}}",
      goTo: "Go to",
      when: "when",
      show: "Show step",
      openAgent: "Open About this agent",
      technical: "Technical detail",
      value: {
        request: "The user's request",
        answer: "Answer of ‘{{step}}’",
        result: "Result of ‘{{step}}’",
        documents: "Documents found by ‘{{step}}’",
        reply: "Reply to ‘{{step}}’",
        decision: "Decision on ‘{{step}}’",
        subagent: "Result of ‘{{step}}’",
        toolRequest: "System request from ‘{{step}}’",
      },
      cond: {
        rawOnly: "This condition uses things the visual builder can't show, so it is edited as an expression.",
        raw: "Write the expression myself",
        always: "Always (no condition).",
        n: "Condition {{n}}",
        and: "and",
        value: "Value for condition {{n}}",
        pickValue: "Choose a value from an earlier step",
        op: "Comparison for condition {{n}}",
        target: "Compare condition {{n}} with",
        remove: "Remove condition {{n}}",
        add: "Add a condition",
        ops: {
          filled: "is filled in",
          empty: "is empty",
          equals: "equals",
          not_is: "is not",
          not_equals: "is filled and is not",
          more_than: "is more than",
          at_least: "is at least",
          less_than: "is less than",
          at_most: "is at most",
          approved: "was approved",
          rejected: "was rejected",
          within: "is within",
          not_within: "is not within",
        },
        withinOf: "{{tolerance}} of {{other}}",
        withinOther: "Compared with",
        withinTolerance: "Give or take (0.5, 2% or 3 days)",
        problem: {
          key: "Choose a value.",
          value: "Enter what to compare with.",
          number: "Enter a number.",
        },
      },
      args: {
        noSchema: "This action doesn't describe its inputs, so they are written as JSON.",
        true: "Yes",
        false: "No",
        fromStep: "Value from an earlier step",
        fromStepFor: "Use a value from an earlier step for {{field}}",
        missing: "Still needed: {{fields}}",
        extra: "Not described by this action; kept as it is.",
        raw: "Edit as JSON",
        bytes: "{{n}} bytes",
        kb: "{{n}} KB",
        setCode: "Set by the developer (code, {{size}})",
        setData_one: "Set by the developer ({{count}} value)",
        setData_other: "Set by the developer ({{count}} values)",
        view: "View or edit",
        viewAria: "View or edit {{field}}",
        codeHelp: "Written by a developer. Change it only if you know what it does; the step uses it exactly as written.",
        invalidJson: "This isn't valid JSON yet.",
        save: "Save",
        cancel: "Cancel",
        advanced_one: "Advanced ({{count}} setting)",
        advanced_other: "Advanced ({{count}} settings)",
        sep: ", ",
        groupFrom: "Or take all of it from an earlier step:",
        byField: "Fill in field by field",
        listHint: "Type each one and press Enter, or separate them with commas",
      },
      argName: {
        lang: "Language",
        request: "The request",
        discovery: "Clubs found",
        search: "Clubs to check",
        max_clubs: "How many clubs",
        target: "Day to show",
        url: "Page to open",
        urls: "Pages to open",
        pages: "Pages to open",
        script: "Page reading code",
        script_args: "Values for the page reading code",
        script_timeout_s: "Time limit for the page reading code (seconds)",
        steps: "What to do on the page",
        code: "Code to run",
        files: "Code files",
        input: "Values for the code",
        stdin: "Text input for the code",
        output: "Result format",
        timeout_s: "Time limit (seconds)",
        memory_mb: "Memory limit (MB)",
        invoice_id: "Invoice number",
        amount: "Amount",
        currency: "Currency",
        pay_on: "Payment date",
        invoice: "Invoice",
        query: "Search for",
        question: "Question",
        status: "Status",
        date: "Date",
        city: "City",
        to: "Send to",
        subject: "Subject",
        body: "Details",
        note: "Note",
        cc: "Copy to",
        body_text: "Message",
        destination: "Where to send it",
        event_type: "Kind of event",
        payload: "What it carries",
        subject_key: "What the person approves",
      },
      outName: { steps: "Steps" },
      enumValue: { approve: "Approved", reject: "Rejected", edit: "Changed, then approved", yes: "Yes", no: "No", true: "Yes", false: "No" },
      roleName: {
        "code-runner": "Code runner",
        "browser-operator": "Browser operator",
        "file-editor": "File editor",
        "directory-operator": "Directory operator",
      },
      ref: {
        fromStep: "From step ‘{{step}}’ → {{field}}",
        requestField: "From the user's request → {{field}}",
        unknown: "{{field}}",
      },
      edge: {
        if: "if {{condition}}",
        clause: "{{subject}} {{op}} {{value}}",
        ok: "‘{{step}}’ worked",
        not_ok: "‘{{step}}’ did not work",
        toolCall: "when the AI asks {{tool}}: {{action}}",
        noToolCall: "when the AI has its answer",
        condition: "a condition",
      },
      field: {
        label_en: "Step name (English)",
        label_ar: "Step name (Arabic)",
        id: "Technical id",
        promptRef: "Saved prompt (optional)",
        template: "Instructions for the AI",
        variables: "Values used in the instructions",
        llmTools: "Systems the AI may use here",
        tool: "System",
        action: "What it does",
        args: "What to send",
        routes: "Where to go next",
        default: "Otherwise go to",
        subjectKey: "What the person approves",
        approverRoles: "Who approves",
        sla: "Decide within",
        onReject: "If rejected, go to",
        question_en: "Question to ask (English)",
        question_ar: "Question to ask (Arabic)",
        replyKey: "Save the reply as",
        branches: "Run these together",
        join: "Then continue at",
        strategy: "Combine the results by",
        agent: "Other agent",
        agentVersion: "Version",
        collection: "Knowledge to search",
        query: "Search for",
        entrypoint: "Code to run",
        when: "Go this way when",
        outputKey: "Save the result as",
        maxIterations: "Repeat at most",
      },
      help: {
        id: "Made from the English name. Lowercase letters, digits and _; renaming updates every connection.",
        promptRef: "A prompt from the prompt registry, e.g. prompts:/invoice-system@production. It replaces the instructions above.",
        template: "Say what this step should do. {name} inserts one of the values below.",
        variables: "Values from earlier steps the instructions use.",
        llmTools: "Only systems this agent is allowed to use are listed.",
        tool: "Choosing a system here also allows the agent to use it.",
        action: "Actions marked 'changes data' can be put behind an approval.",
        args: "Fill in each value, or take it from an earlier step.",
        routes: "Checked from top to bottom; the first that matches is used.",
        default: "Used when none of the ways above match.",
        subjectKey: "The value the approver sees and decides on.",
        approverRoles: "Anyone with one of these roles can decide. The requester can never approve.",
        sla: "In working hours. Half-way a reminder goes out; if you name who it goes to next, it moves on to them when the time is up.",
        onReject: "Leave empty to stop the run when the request is rejected.",
        replyKey: "A name later steps can use to read the reply.",
        query: "Usually the user's request.",
        when: "Leave empty to always go this way.",
        outputKey: "A name later steps use to read this result. Empty means \"{{fallback}}\".",
        maxIterations: "How often this step may run in one request; stops endless loops. When it is reached, the run goes where “If the limit is reached, go to” says, or stops with a plain message and keeps the last answer.",
      },
      nodeHelp: {
        llm: "The AI reads the instructions and earlier results, then answers or asks a system for help.",
        tool: "Uses a system (for example the ERP) to look something up or make a change.",
        router: "Chooses where to go next based on earlier results.",
        human_approval: "Waits until a person approves or rejects.",
        human_input: "Asks the user a question and waits for the reply.",
        parallel: "Runs several steps at the same time.",
        join: "Waits for parallel steps and combines their results.",
        subgraph: "Hands the work to another agent.",
        retrieval: "Searches a knowledge collection for relevant documents.",
        python: "Runs reviewed code (not available to agents made in the guided builder).",
        end: "The run finishes here.",
      },
      role: {
        approver: "Approvers",
        "finance-approver": "Finance approvers",
        "finance-director": "Finance directors",
        cfo: "Chief financial officer",
        reviewer: "Reviewers",
        "case-supervisor": "Case supervisors",
        "privacy-officer": "Privacy officers",
        "release-approver": "Release approvers",
        "tenant-admin": "Organisation administrators",
      },
      sla: {
        PT1H: "1 hour",
        PT4H: "4 hours",
        PT8H: "8 hours",
        P1D: "1 day",
        P3D: "3 days",
      },
      issue: {
        toolNotAllowed: "Step ‘{{step}}’ uses {{tool}} ({{action}}), but this agent isn't allowed to use it yet — allow it under About this agent › Allowed systems.",
        promptBoth: "Step ‘{{step}}’ has both its own instructions and a saved prompt. Keep one: clear the instructions or the saved prompt.",
        promptNone: "Step ‘{{step}}’ has no instructions. Write what the AI should do, or pick a saved prompt.",
        parallelMixed: "Step ‘{{step}}’ runs AI or code steps at the same time as system, person or knowledge steps. These can't run together: put them in separate groups.",
        llmToolNotAllowed: "Step ‘{{step}}’ offers {{tool}} to the AI, but this agent isn't allowed to use it.",
        collectionNotDeclared: "Step ‘{{step}}’ searches {{collection}}, which isn't in this agent's knowledge yet.",
        approvalNotAllowed: "An approval rule covers {{tool}} ({{action}}), which this agent isn't allowed to use.",
        approvalLevel_noRole: "The approval before {{tool}} ({{action}}): level {{level}} needs at least one role.",
        approvalLevel_noPerson: "The approval before {{tool}} ({{action}}): level {{level}} needs the value that names the person.",
        approvalLevel_quorumNotRoles: "The approval before {{tool}} ({{action}}): only a level by role can ask several people (level {{level}}).",
        approvalLevel_tooMany: "The approval before {{tool}} ({{action}}) has more than 6 levels.",
        approvalStepLevel_noRole: "‘{{step}}’: level {{level}} needs at least one role.",
        approvalStepLevel_noPerson: "‘{{step}}’: level {{level}} needs the value that names the person.",
        approvalStepLevel_quorumNotRoles: "‘{{step}}’: only a level by role can ask several people (level {{level}}).",
        approvalStepLevel_tooMany: "‘{{step}}’ has more than 6 approval levels.",
        unreachable: "These steps can never be reached from the start: {{steps}}. Connect them or delete them.",
        edgeUnknown: "A connection from ‘{{from}}’ points to a step that doesn't exist ({{to}}).",
        limitUnknown: "Step ‘{{step}}’ goes to a step that doesn't exist when its repeat limit is reached. Pick another step under “If the limit is reached, go to”.",
        limitSelf: "Step ‘{{step}}’ can't go back to itself when its repeat limit is reached. Pick another step.",
        limitNeedsRepeat: "Step ‘{{step}}’ says where to go when the limit is reached, but has no limit. Set “Repeat at most”, or clear “If the limit is reached, go to”.",
        failUnknown: "Step ‘{{step}}’ goes to a step that doesn't exist when it fails. Pick another step under “If it still fails, go to”.",
        failSelf: "Step ‘{{step}}’ can't go back to itself when it fails. Use “Try again” instead, or pick another step.",
        waitNothing: "Step ‘{{step}}’ waits for nothing. Choose an event, a time or a person's answer.",
        calcTwice: "Step ‘{{step}}’ works out {{values}} more than once. Give each value its own name.",
        loopList: "Step ‘{{step}}’ has no list yet. Choose the list it goes through.",
        tableMatch: "Step ‘{{step}}’ needs the two tables to match and at least one column to match on.",
        tableGroup: "Step ‘{{step}}’ needs the rows and the columns to group by, or a total.",
        loopBody: "Step ‘{{step}}’ does nothing for each item yet. Add a step on its ‘for each item’ arrow, or choose another agent.",
        loopArrow: "The arrow from ‘{{from}}’ to ‘{{to}}’ leaves the steps done for each item by ‘{{step}}’. Steps done for each item only lead to each other; the run goes on after ‘{{step}}’ itself.",
        calcEmpty: "Step ‘{{step}}’: ‘{{value}}’ has no formula yet. Choose what to work out.",
        calcFormula: "Step ‘{{step}}’: the formula of ‘{{value}}’ isn't finished. Check its brackets and quotes.",
        waitEventName: "Step ‘{{step}}’ waits for an event but has no event name.",
        waitEventKey: "Step ‘{{step}}’ waits for an event but doesn't say which one belongs to this run. Pick the value both sides know, e.g. the order number.",
        waitAt: "Step ‘{{step}}’ waits until a date and time but none is chosen.",
        waitGiveUpSelf: "Step ‘{{step}}’ can't go back to itself when nothing comes in time. Pick another step.",
        entryMissing: "The first step ({{id}}) doesn't exist. Pick a step and choose \"Make entry node\".",
        duplicateIds: "Two steps have the same technical id: {{ids}}.",
        badId: "The technical id ‘{{id}}’ isn't allowed: use lowercase letters, digits and _.",
        agentName: "The agent's technical name isn't valid.",
        agentVersion: "The version must look like 1.0.0.",
        autonomous: "Agents that act on their own are limited to risk tier 2.",
        nodeFieldMissing: "Step ‘{{step}}’ still needs {{field}}.",
        nodeFieldInvalid: "Step ‘{{step}}’ has an invalid value for {{field}}.",
        nodeInvalid: "Step ‘{{step}}’ isn't complete yet.",
        nodeLabel: "Give step ‘{{step}}’ a name in {{lang}} too, or leave both names empty.",
        agentText: "Fill in the agent's name, description and goal in both languages.",
        other: "Technical check: {{detail}}",
        dataLevel: "{{tool}} holds {{level}} data, but this agent may handle only {{agentLevel}} data, so its calls would be refused. Raise the agent's data level to {{level}}.",
        riskTier: "Step ‘{{step}}’ needs risk level {{tier}}, but this agent is at level {{agentTier}}, so it would be refused.",
        riskTierNocode: "Step ‘{{step}}’ needs risk level {{tier}}, above the guided builder's limit of 2. Use another action, or ask a developer to build this agent in code.",
        roleNeeded: "Step ‘{{step}}’ needs the {{roles}} role, which you don't have. Ask an administrator for it, or have someone with the role try the agent.",
        modelLevel: "{{model}} may not see {{level}} data (it may see up to {{top}}). Choose another model or a lower data level.",
        modelLevelStep: "Step ‘{{step}}’ uses {{model}}, which may not see {{level}} data (up to {{top}}). Choose another model for this step.",
        modelMissing: "The agent's AI model ({{model}}) isn't available on this platform, so it can't answer. Choose another model under About this agent.",
        modelMissingStep: "Step ‘{{step}}’ uses an AI model ({{model}}) that isn't available on this platform. Choose another model for this step.",
        collectionMissing: "The knowledge {{collection}} doesn't exist in your organisation, so searching it finds nothing. Pick an existing collection, or add it under Knowledge first.",
        promptMissing: "Step ‘{{step}}’ uses the saved prompt ‘{{prompt}}’, which isn't in the prompt library. Pick another saved prompt, or write the instructions in the step.",
        unusedTeam: "This agent's file has a “debate” team of agents. That kind of team no longer runs: the steps on the graph decide what happens. Remove it to avoid confusion.",
        unusedCard: "This agent's file asks to publish a card for other agents to find it. That isn't available yet, so nothing is published.",
        teamStale: "The team's steps don't match the team any more (they don't ask {{members}}). Build the steps again from the team.",
        teamNoDescription: "Team member ‘{{member}}’ has no description. Say what it is good at, so the team knows when to ask it.",
        cardNoSkills: "The agent card lists nothing the agent can do, so other agents see only its description. Add what it can do under About this agent › Share with other agents.",
        unusedEscalate: "An approval rule says who to pass the request on to when nobody decides in time. That isn't available yet: the request just waits.",
        unusedFallback: "A backup AI model is set. It isn't used: the platform picks a backup itself when a model is down.",
      },
      fieldName: {
        tool: "a system",
        action: "what it does",
        collection: "the knowledge to search",
        prompt: "instructions for the AI",
        subjectKey: "what the person approves",
        question: "the question to ask",
        outputKey: "a name for the result",
        routes: "where to go next",
        default: "where to go otherwise",
        branches: "steps to run together",
        join: "where to continue",
        agent: "the other agent",
        entrypoint: "the code to run",
        args: "what to send",
        maxIterations: "a repeat limit",
      },
      fix: {
        grant: "Allow it",
        knowledge: "Add it",
        dropLlmTool: "Remove it from this step",
        level: "Raise to {{level}}",
        tier: "Raise to level {{tier}}",
        clear: "Remove it",
        rebuildTeam: "Build the steps again",
      },
    },
    prompts: {
      newName: "New prompt name",
      create: "New prompt",
      createHelp: "Letters, digits, dots, hyphens and underscores. The first version is created when you save.",
      none: "No prompts in this tenant yet. Create one to start.",
      nameInvalid: "Use letters, digits, dots, hyphens and underscores (start with a letter or digit).",
      select: "Prompt",
      pick: "Select version {{version}}",
      version: "Version",
      aliases: "Aliases",
      message: "Change note",
      created: "Created",
      alias: "Alias",
      setAlias: "Point alias to v{{version}}",
      aliasSet: "{{alias}} now points to v{{version}}",
      editor: "Edit as a new version",
      template: "Template",
      commit: "Change note",
      save: "Save new version",
      saved: "New version saved",
    },
    runner: {
      select: "Recorded run",
      play: "Replay",
      pause: "Pause",
      position: "Event {{pos}} of {{total}}",
      messages: "Messages",
      sources: "Sources",
      activity: "Activity",
      events: "AG-UI events",
    },
    team: {
      title: "Works as a team",
      intro: "A team answers by asking other agents of your organisation. Each member keeps its own rules, approvals and limits, and its work shows inside the team's run.",
      switch: "This agent works as a team of agents",
      replaceSteps: "The team's steps replace the steps drawn now. Continue?",
      replaceOk: "Use a team",
      replaceCancel: "Keep my steps",
      builtFromTeam: "The steps are built from the team; editing them by hand is not needed.",
      teamOfTeams: "A member can itself be a team: that is how you make a team of teams.",
      hierarchical: "This agent's file says “teams under a lead”: it works as a supervisor whose members are teams.",
      debate: "This agent's file has a “debate” team. That kind of team no longer runs.",
      debateRemove: "Remove it",
      kindQuestion: "How do the members work together?",
      kind: {
        supervisor: { title: "A supervisor", body: "A supervisor asks the members and combines their answers." },
        handoff: { title: "Hand over", body: "One member helps at a time and can hand the conversation to another." },
      },
      add: "Add a member",
      addHelp: "Agents released in your organisation. Each one is asked only for the work you describe.",
      addPlaceholder: "Choose an agent",
      noAgents: "No released agents yet",
      empty: "Add at least two members.",
      notReleased: "Not released",
      remove: "Remove",
      removeAria: "Remove {{member}} from the team",
      role_en: "Name in the team (English)",
      role_ar: "Name in the team (Arabic)",
      description_en: "What it is good at (English)",
      description_ar: "What it is good at (Arabic)",
      descriptionHelp: "Plain words, for example “Invoices, payments and refunds”. The team reads this to choose who helps.",
      handsTo: "May hand the conversation to",
      handsToHelp: "When this member can't help, it can pass the person to these members (with a reason).",
      handsToNobody: "Nobody",
      technicalName: "Agent: {{name}}",
      limit: { supervisor: "Most times the supervisor asks a member, per request", handoff: "Most hand-overs per request" },
      limitHelp: {
        supervisor: "After that the answers so far are combined, so a request never goes round in circles.",
        handoff: "After that the member who has the conversation keeps it.",
      },
      howItRuns: "Members run with their own rights, checks and approvals; a member the person may not use is skipped and the team is told why.",
    },
    card: {
      title: "Share with other agents (agent card)",
      intro: "An agent card tells other agents and programs of your organisation what this agent can do, so they can ask it (the A2A standard).",
      switch: "Publish this agent's card",
      skills: "What it can do",
      skillsHelp: "Each item is one thing other agents can ask it for.",
      skillName_en: "Name (English)",
      skillName_ar: "Name (Arabic)",
      skillDescription_en: "Description (English)",
      skillDescription_ar: "Description (Arabic)",
      tags: "Keywords",
      tagsHelp: "Words other agents can search for.",
      addSkill: "Add something it can do",
      removeSkill: "Remove",
      preview: "What other agents read",
      previewName: "Name",
      previewDescription: "Description",
      previewLevel: "Data it handles",
      previewLanguages: "Languages",
      previewRisk: "Risk level",
      languages: "Arabic and English",
      noSkills: "Nothing listed yet: other agents see only the description.",
      unnamed: "(no name yet)",
      where: "Other agents read the card here, with an organisation API key that lists this agent (Channels › Connect apps):",
      callAt: "They send it requests here:",
      released: "The card shows the version released in each environment; a draft has no card.",
    },
    runStatus: { idle: "Not started", running: "Running", awaiting_approval: "Awaiting approval", finished: "Finished", error: "Failed", cancelled: "Cancelled" },
    docs: {
      title: "Agents as files",
      intro: "Everything you build here is saved as one agent file. Developers can download it (Steps › Download as a file), keep it with their code, check it and turn it into a picture with the command-line tool below, then load it again here.",
      cli: "Command-line tool (ap)",
      cliHelp: "Run these where the agent platform's developer kit is installed. Replace my-agent.yaml with your agent file.",
      cmd: { validate: "Check an agent file for mistakes", render: "Draw its steps as a picture (Arabic labels)", init: "Start a new agent file", patterns: "List the example shapes" },
      patternsTitle: "Example shapes",
      patternsHelp: "Open one under Steps › Examples to start from it.",
    },
  },
  ar: {
    title: "منشئ الوكلاء",
    subtitle: "ابنِ الوكلاء خطوة بخطوة وجرّبهم وأطلقهم.",
    tab: { graph: "الخطوات", prompts: "التعليمات المحفوظة", runner: "مشغل الاختبار", docs: "للمطورين", flow: "كيف جرى العمل" },
    tabHelp: { graph: "ارسم خطوات الوكيل وغيّرها، وحدد ما يُسمح له باستخدامه.", prompts: "تعليمات للذكاء الاصطناعي محفوظة في مكتبة، لتتشاركها عدة خطوات أو وكلاء.", try: "اسأل الوكيل وشاهد كل خطوة تعمل، دون التأثير على الأنظمة الحقيقية.", tests: "أسئلة محفوظة مع الإجابات المتوقعة، تُعاد قبل كل إطلاق.", triggers: "متى يبدأ الوكيل من تلقاء نفسه: حسب جدول أو عندما يستدعيه نظام آخر.", flow: "كيف مرّت الطلبات الحقيقية بالخطوات: أين انتظرت أو فشلت أو انتهت.", docs: "للمطورين الذين يكتبون الوكلاء كملفات: أدوات سطر الأوامر ومرجع الملف." },
    tier: "المستوى {{tier}}",
    valid: "لا توجد مشكلات",
    issues: "{{n}} مشكلة",
    issuesTitle: "مشكلات التحقق",
    spec: { "invoice-assistant": "مثال: مساعد الفواتير", react: "مثال: يفكّر ويستخدم نظامًا ويكرر", router: "مثال: يصنّف الطلب ثم يجيب بالطريقة المناسبة", plan_execute: "مثال: يخطط أولًا ثم ينفذ كل جزء", reflection: "مثال: يكتب ثم يراجع ثم يحسّن", supervisor: "مثال: مشرف يسأل فريقًا من الوكلاء" },
    meta: { name: "الاسم", description: "الوصف", pattern: "كيف رُتّبت خطواته", patternHelp: "وصف قصير لشكل الوكيل يظهر في القوائم. لا يغيّر ما يفعله الوكيل: الخطوات في المخطط هي التي تحدد ذلك.", risk: "مستوى المخاطر", validation: "الفحوصات" },
    pattern: { react: "يفكّر ويستخدم نظامًا ويكرر", plan_execute: "يخطط أولًا ثم ينفذ كل جزء", reflection: "يكتب ثم يراجع ثم يحسّن", router: "يصنّف الطلب ثم يجيب بالطريقة المناسبة", supervisor: "مشرف يسأل وكلاء آخرين ويجمع إجاباتهم", handoff: "يساعد وكيل واحد في كل مرة ويمكنه تسليم المحادثة", debate: "عدة وكلاء يتناقشون وواحد يقرر", hierarchical: "فرق من الوكلاء تحت وكيل رئيسي", custom: "خطوات خاصة به" },
    riskHelp: { 1: "المستوى 1: يستخدم أقل الإجراءات خطورة فقط، وغالبًا القراءة والإجابة.", 2: "المستوى 2: يستخدم أيضًا إجراءات تغيّر البيانات بشكل محدود. أعلى مستوى للوكلاء المصنوعين في المنشئ الموجَّه.", 3: "المستوى 3: قد يغيّر بيانات مهمة. تنتظر تغييراته موافقة شخص (ما لم يحدد المسؤول غير ذلك)، ويعمل دائمًا لصالح شخص، ويحتاج تشغيله الفعلي إلى موافقة شخصين.", 4: "المستوى 4: أكثر الإجراءات حساسية، مع ضمانات المستوى 3 نفسها." },
    node: { llm: "نموذج", tool: "أداة", router: "موجّه", human_approval: "اعتماد بشري", human_input: "إدخال بشري", parallel: "متوازٍ", join: "دمج", subgraph: "وكيل فرعي", retrieval: "استرجاع", python: "بايثون", decision: "قرار", end: "إنهاء", wait: "انتظار", calculate: "حساب", foreach: "لكل عنصر", table: "جدول" },
    graph: {
      select: "الوكيل المراد فتحه",
      figure: "مخطط الوكيل {{name}}",
      loop: "تكرار ≤ {{n}}",
      entry: "البداية",
      json: "ملف الوكيل (للمطورين)",
      controls: "أدوات التحكم بالمخطط",
      zoomIn: "تكبير",
      zoomOut: "تصغير",
      fit: "ملاءمة العرض",
      interactive: "تبديل التفاعل",
      nodeHelp: "عقدة في المخطط",
      edgeHelp: "رابط في المخطط",
    },
    about: {
      versionPlain: "يظهر للناس كالإصدار {{version}}.",
      ownerHelp: "الفريق أو الشخص المسؤول عن هذا الوكيل. يظهر في قائمة الوكلاء.",
      constraints: "قواعد يجب أن يلتزم بها دائمًا",
      constraintsHelp: "تُعطى للذكاء الاصطناعي في كل خطوة مع دوره وأهدافه. مثال: «لا تشارك راتب أي موظف أبدًا».",
      constraintN: "القاعدة {{n}}",
      constraintAr: "القاعدة {{n}} (بالعربية)",
      constraintEn: "القاعدة {{n}} (بالإنجليزية)",
      removeConstraint: "حذف القاعدة {{n}}",
      addConstraint: "إضافة قاعدة",
      noConstraints: "لا توجد قواعد بعد.",
      temperature: "الإبداع (0 دقيق، 1 مبدع)",
      temperatureHelp: "القيمة الأقل تعطي الإجابة نفسها كل مرة؛ والأعلى تعطي صياغة أكثر تنوعًا. يمكن لكل خطوة أن تحدد قيمتها.",
      answerTokens: "أطول إجابة (رموز)",
      answerTokensHelp: "الطول المسموح لإجابة واحدة من الذكاء الاصطناعي. نحو 750 كلمة إنجليزية لكل 1000 رمز. يمكن لكل خطوة أن تحدد قيمتها.",
      topK: "المقاطع المُمرَّرة من {{collection}}",
      topKHelp: "عدد أفضل المقاطع المطابقة التي تمررها خطوة البحث (من 1 إلى 50). العدد الأكبر يعطي الذكاء الاصطناعي ما يقرؤه أكثر، لكنه يكلّف أكثر.",
      approvalRule: "قبل «{{action}}» في {{tool}}",
      approvalWho: "من يوافق",
      approvalWithin: "يقرر خلال",
      approvalWithinHelp: "المدة التي ينتظرها الطلب في صندوق الموافقين.",
      approvalEscalate: "إذا لم يقرّر أحد في الوقت، أرسله إلى",
      approvalEscalateHelp: "في منتصف المدة يُرسل تذكير؛ وعند انتهائها ينتقل الطلب إلى الدور التالي من هذه الأدوار بالترتيب، ولكلٍّ منها المدة نفسها من جديد. إذا تُرك فارغًا: يبقى مع الموافقين ويُعلَّم بأنه متأخر.",
      approvalEscalateNone: "لا أحد غيرهم (يبقى ويُعلَّم بأنه متأخر)",
      removeRule: "حذف هذه الموافقة",
      addRule: "اطلب موافقة قبل…",
      addRuleHelp: "اختر إجراءً يغيّر البيانات. يجب أن يوافق شخص قبل أن ينفذه الوكيل.",
      rulesHelp: "تُضاف هذه إلى الموافقات التي تطلبها مؤسستك دائمًا؛ ولا تلغي أيًا منها.",
      groups: "لا يستخدمه إلا هذه المجموعات",
      groupsHelp: "اتركه فارغًا ليستخدمه كل من يُسمح له باستخدام الوكلاء. اكتب اسم المجموعة واضغط Enter.",
      maxSteps: "الخطوات لكل طلب",
      maxStepsHelp: "يتوقف الطلب بعد هذا العدد من الخطوات.",
      timeLimit: "المهلة (بالدقائق)",
      timeLimitHelp: "يتوقف الطلب إذا استغرق وقتًا أطول.",
      maxTokens: "نص الذكاء الاصطناعي لكل طلب (رموز)",
      maxTokensHelp: "مقدار النص الذي يقرؤه الذكاء الاصطناعي ويكتبه في طلب واحد.",
      autonomousGuided: "يتيح لجدوله أو حدثه بدء تشغيله دون أن يطلب شخص. ويبقى وكيلًا موجّهًا؛ وتُراجع التغييرات التي يجريها كما تنص قواعد الاعتماد.",
      name: "الاسم التقني",
      nameHelp: "أحرف لاتينية صغيرة وأرقام وشرطات",
      version: "الإصدار",
      description_ar: "ماذا يفعل (بالعربية)",
      description_en: "ماذا يفعل (بالإنجليزية)",
      role_ar: "دوره (بالعربية)",
      role_en: "دوره (بالإنجليزية)",
      model: "نموذج الذكاء الاصطناعي",
      knowledge: "المعرفة التي يبحث فيها",
      knowledgeHelp: "مجموعات المستندات التي يمكن لهذا الوكيل قراءتها. تستخدمها الخطوات التي تبحث في المعرفة.",
      maxCost: "حد الإنفاق لكل طلب (دولار)",
      maxCostHelp: "يتوقف الطلب إذا كانت تكلفته ستتجاوز هذا الحد.",
      autonomous: "يعمل وحده",
      autonomousHelp: "يتيح لجدوله أو لنظام آخر تشغيله دون أن يطلب شخص. فعّله فقط بعد مراجعة ما يفعله؛ ويُعد بعدها وكيلًا راجعه مطوّر ويُفتح هنا لا في المنشئ الموجّه.",
      autonomousTier: "الوكلاء عالو المخاطر (المستوى 3 أو 4) يعملون دائمًا لصالح شخص.",
    },
    channel: { web: "الويب", api: "واجهة برمجية", widget: "أداة الموقع", email: "البريد الإلكتروني", chat_app: "تطبيق محادثة", chat: "تطبيق محادثة", voice: "الصوت", mcp: "أدوات ذكاء اصطناعي أخرى (MCP)" },
    cert: { none: "غير معتمدة", bronze: "برونزي", silver: "فضي", gold: "ذهبي" },
    source: { schema: "المخطط", semantic: "قاعدة", nocode: "بدون برمجة" },
    pick: {
      versionN: "الإصدار {{version}}",
      new: "جديد",
      newAgent: "وكيل جديد (فارغ)",
      examples: "أمثلة",
      registry: "الوكلاء المسجلون",
      version: "الإصدار",
      state: { draft: "مسودة", submitted: "محفوظ" },
      noVersions: "لا توجد إصدارات لهذا الوكيل بعد.",
      registryUnavailable: "سجل الوكلاء غير متاح؛ يمكن فتح الأمثلة فقط.",
      loadFailed: "تعذّر تحميل الوكيل",
    },
    save: {
      saveAs: "احفظه كالإصدار {{version}}",
      saveAsHint: "يحفظ تغييراتك كإصدار قابل للإطلاق. الإصدار المحفوظ لا يتغير أبدًا؛ والتغييرات اللاحقة تصبح الإصدار التالي.",
      savedAs: "حُفظ كالإصدار {{version}}",
      savedHint: "هذا الإصدار محفوظ ولا يمكن تغييره. غيّر أي شيء لحفظ الإصدار التالي.",
      submittedAs: "حُفظ كالإصدار {{version}}",
      draftedAs: "حُفظت مسودة الإصدار {{version}}",
      releaseDev: "أطلق الإصدار {{version}} إلى مرحلة التطوير",
      releaseReason: "أُطلق من محرر المخطط لتجربته",
      released: "يعمل الإصدار {{version}} الآن في مرحلة التطوير",
      releaseWaiting: "ينتظر الإصدار {{version}} إطلاقه",
      releaseFailed: "لم ينجح الإطلاق",
      draft: "حفظ كمسودة",
      submit: "احفظه كإصدار",
      drafted: "حُفظت المسودة: {{ref}}",
      submitted: "قُدِّم الإصدار: {{ref}}",
      immutableTitle: "هذا الإصدار محفوظ ولا يمكن تغييره",
      bumpRequired: "الإصدار {{version}} محفوظ مسبقًا. استخدم الإصدار التالي لحفظ تغييراتك.",
      rejected: "رفض السجل المواصفة",
      forbidden: "تحتاج إلى دور المنشئ أو مسؤول الوكلاء لحفظ الوكلاء",
      failed: "تعذّر الحفظ",
    },
    editor: {
      region: "محرر المخطط",
      palette: "إضافة عقدة",
      addNode: "إضافة عقدة {{type}}",
      undo: "تراجع",
      undoHint: "Ctrl+Z للتراجع، Ctrl+Shift+Z للإعادة",
      redo: "إعادة",
      deleteSelected: "حذف المحدد",
      autoLayout: "ترتيب تلقائي",
      unsaved: "تغييرات غير محفوظة",
      canvasHelp: "اسحب الخطوة لتحريكها. اسحب من النقطة على حافة خطوة إلى خطوة أخرى لإضافة سهم. حدد خطوة أو سهمًا واضغط Delete لحذفه. ويمكن فعل كل شيء من اللوحة الجانبية أيضًا.",
      downloadHelp: "يحفظ الوكيل كملف (JSON) يمكن للمطورين الاحتفاظ به في الشيفرة أو تحميله مجددًا.",
      nodeAria: "عقدة {{kind}} {{label}}",
      edgeAria: "رابط من {{from}} إلى {{to}}",
      nodeHelp: "اضغط Enter أو المسافة للتحديد، والأسهم للتحريك، وDelete للحذف.",
      edgeHelp: "اضغط Enter أو المسافة للتحديد، وDelete للحذف.",
      hasIssues: "بها مشكلات",
      showNode: "عرض {{id}}",
      selection: "التحديد",
      agent: "عن هذا الوكيل",
      download: "تنزيل كملف",
      nothingSelected: "حدّد خطوة أو سهمًا في المخطط، أو استخدم + على سهم لإضافة خطوة.",
      setEntry: "اجعلها عقدة البداية",
      isEntry: "عقدة البداية",
      deleteNode: "حذف العقدة",
      deleteEdge: "حذف الرابط",
      edge: "رابط",
      idHelp: "أحرف إنجليزية صغيرة وأرقام و- أو _. إعادة التسمية تحدّث جميع الروابط.",
      idInvalid: "استخدم أحرفًا إنجليزية صغيرة وأرقامًا و _ تبدأ بحرف.",
      idTaken: "توجد عقدة أخرى بهذا المعرّف.",
      promptRefHelp: "موجّه من السجل، مثل prompts:/invoice-system@production",
      llmToolsHelp: "الأدوات الممنوحة التي يمكن للنموذج استدعاؤها.",
      toolHelp: "اختيار أداة وإجراء يمنحهما في المواصفة.",
      pickTool: "اختر أداة",
      pickAction: "اختر إجراءً",
      argsHelp: "كائن JSON؛ يمكن أن تشير القيم إلى حالة التشغيل (state.key).",
      argsInvalid: "يجب أن تكون الوسائط كائن JSON.",
      routeWhen: "شرط المسار {{n}}",
      routeTo: "وجهة المسار {{n}}",
      removeRoute: "إزالة المسار {{n}}",
      addRoute: "إضافة مسار",
      slaHelp: "مدة بصيغة ISO 8601، مثل PT4H",
      branchesHelp: "عقدتان على الأقل تعملان بالتوازي.",
      agentHelp: "اختر وكيلاً مسجلاً آخر وأحد إصداراته المقدَّمة.",
      collectionHelp: "اختيار مجموعة يعلنها ضمن معرفة الوكيل.",
      entrypointHelp: "module.path:function",
      whenHelp: "شرط على حالة التشغيل؛ الفارغ يعني دائمًا.",
      outgoing: "الروابط الخارجة",
      noOutgoing: "لا توجد روابط خارجة.",
      connectTo: "اربط بعقدة",
      connect: "ربط",
      bump: "استخدم الإصدار التالي",
      goals: "الأهداف",
      goalN: "الهدف {{n}}",
      goalAr: "الهدف {{n}} (بالعربية)",
      goalEn: "الهدف {{n}} (بالإنجليزية)",
      removeGoal: "إزالة الهدف {{n}}",
      addGoal: "إضافة هدف",
      grants: "الأدوات الممنوحة",
      noGrants: "لا توجد أنظمة مسموحة بعد. إضافة خطوة «استخدام نظام» تسمح بالنظام الذي تستخدمه.",
      field: {
        id: "معرّف العقدة",
        label_ar: "التسمية (بالعربية)",
        label_en: "التسمية (بالإنجليزية)",
        promptRef: "مرجع الموجّه",
        template: "قالب الموجّه",
        variables: "متغيرات الموجّه",
        llmTools: "الأدوات التي يمكن للنموذج استدعاؤها",
        outputKey: "مفتاح المخرجات",
        tool: "الأداة",
        action: "الإجراء",
        args: "الوسائط",
        routes: "المسارات",
        default: "المسار الافتراضي",
        subjectKey: "مفتاح الموضوع (في الحالة)",
        approverRoles: "أدوار المعتمدين",
        sla: "مهلة الاعتماد",
        onReject: "عند الرفض انتقل إلى",
        question_ar: "السؤال (بالعربية)",
        question_en: "السؤال (بالإنجليزية)",
        branches: "الفروع",
        join: "عقدة الدمج",
        strategy: "طريقة الدمج",
        agent: "الوكيل الفرعي",
        collection: "المجموعة",
        query: "الاستعلام",
        entrypoint: "نقطة الدخول",
        maxIterations: "الحد الأقصى للتكرارات",
        when: "الشرط (when)",
        owner: "المالك",
        maxClassification: "أكثر البيانات حساسية التي يمكنه التعامل معها",
      },
      strategy: { merge: "دمج", list: "قائمة", vote: "تصويت" },
      classification: { public: "عام", internal: "داخلي", confidential: "سري", restricted: "مقيّد" },
      edgeKind: { edge: "رابط", route: "مسار", default: "افتراضي", branch: "متوازٍ", reject: "مرفوض", low_confidence: "غير متأكد", limit: "إذا بلغ الحد", error: "إذا فشلت", give_up: "إذا لم يصل شيء في الوقت", each: "لكل عنصر" },
      edgeKindHelp: {
        edge: "رابط بين عقدتين.",
        route: "مسار في الموجّه.",
        default: "المسار الافتراضي للموجّه؛ غيّره من إعدادات الموجّه.",
        branch: "فرع متوازٍ؛ غيّره من إعدادات العقدة المتوازية.",
        reject: "وجهة الاعتماد المرفوض؛ غيّرها من إعدادات عقدة الاعتماد.",
        low_confidence: "وجهة القرار غير المؤكد؛ غيّرها من إعدادات عقدة القرار.",
        limit: "وجهة التشغيل بعد أن تتكرر الخطوة بالعدد المسموح؛ غيّرها من حدود الخطوة.",
        error: "وجهة التشغيل عندما يظل النظام يفشل بعد المحاولات؛ غيّرها من «إذا فشلت» في الخطوة.",
        give_up: "وجهة التشغيل عندما لا يصل شيء في الوقت؛ غيّرها في خطوة الانتظار.",
        each: "أول خطوة تُنفَّذ لكل عنصر في القائمة؛ غيّرها في خطوة «لكل عنصر».",
      },
    },
    plain: {
      lang: { ar: "العربية", en: "الإنجليزية" },
      grants: "الأنظمة المسموحة",
      grantsHelp: "ما يُسمح لهذا الوكيل بفعله في الأنظمة الأخرى. لا يمكن للخطوات استخدام إلا ما هو مسموح هنا.",
      revoke: "إلغاء السماح",
      revokeAria: "إلغاء السماح لـ {{tool}}: {{action}}",
      advanced: "إعدادات متقدمة",
      outgoing: "الخطوات التالية",
      toStep: "ثم ‹{{step}}›",
      connection: "من ‹{{from}}› إلى ‹{{to}}›",
      changesData: "يغيّر البيانات",
      route: "المسار {{n}}",
      goTo: "انتقل إلى",
      when: "عندما",
      show: "اعرض الخطوة",
      openAgent: "افتح «عن هذا الوكيل»",
      technical: "تفاصيل تقنية",
      value: {
        request: "طلب المستخدم",
        answer: "إجابة ‹{{step}}›",
        result: "نتيجة ‹{{step}}›",
        documents: "المستندات التي وجدها ‹{{step}}›",
        reply: "الرد على ‹{{step}}›",
        decision: "القرار في ‹{{step}}›",
        subagent: "نتيجة ‹{{step}}›",
        toolRequest: "طلب نظام من ‹{{step}}›",
      },
      cond: {
        rawOnly: "يستخدم هذا الشرط أمورًا لا يمكن للمنشئ المرئي عرضها، لذلك يُحرَّر كتعبير.",
        raw: "أكتب التعبير بنفسي",
        always: "دائمًا (بلا شرط).",
        n: "الشرط {{n}}",
        and: "و",
        value: "قيمة الشرط {{n}}",
        pickValue: "اختر قيمة من خطوة سابقة",
        op: "المقارنة في الشرط {{n}}",
        target: "قارن الشرط {{n}} بـ",
        remove: "إزالة الشرط {{n}}",
        add: "إضافة شرط",
        ops: {
          filled: "مُعبّأ",
          empty: "فارغ",
          equals: "يساوي",
          not_is: "لا يساوي",
          not_equals: "موجود ولا يساوي",
          more_than: "أكبر من",
          at_least: "لا يقل عن",
          less_than: "أقل من",
          at_most: "لا يزيد على",
          approved: "تمت الموافقة عليه",
          rejected: "رُفض",
          within: "ضمن",
          not_within: "ليس ضمن",
        },
        withinOf: "{{tolerance}} من {{other}}",
        withinOther: "مقارنةً بـ",
        withinTolerance: "بزيادة أو نقصان (0.5 أو 2% أو 3 days)",
        problem: {
          key: "اختر قيمة.",
          value: "أدخل ما تريد المقارنة به.",
          number: "أدخل رقمًا.",
        },
      },
      args: {
        noSchema: "لا يصف هذا الإجراء مدخلاته، لذلك تُكتب بصيغة JSON.",
        true: "نعم",
        false: "لا",
        fromStep: "قيمة من خطوة سابقة",
        fromStepFor: "استخدم قيمة من خطوة سابقة للحقل {{field}}",
        missing: "ما زال مطلوبًا: {{fields}}",
        extra: "لا يصفه هذا الإجراء؛ يُحتفظ به كما هو.",
        raw: "التحرير بصيغة JSON",
        bytes: "{{n}} بايت",
        kb: "{{n}} كيلوبايت",
        setCode: "حدّده المطوّر (نص برمجي، {{size}})",
        setData_one: "حدّده المطوّر (قيمة واحدة)",
        setData_other: "حدّده المطوّر ({{count}} قيم)",
        view: "عرض أو تعديل",
        viewAria: "عرض أو تعديل {{field}}",
        codeHelp: "كتبه مطوّر. لا تغيّره إلا إذا كنت تعرف ما يفعله؛ تستخدمه الخطوة كما هو مكتوب تمامًا.",
        invalidJson: "هذا ليس JSON صالحًا بعد.",
        save: "حفظ",
        cancel: "إلغاء",
        advanced_one: "متقدم (إعداد واحد)",
        advanced_other: "متقدم ({{count}} إعدادات)",
        sep: "، ",
        groupFrom: "أو خذه كله من خطوة سابقة:",
        byField: "املأ حقلًا حقلًا",
        listHint: "اكتب كل واحد واضغط Enter، أو افصل بينها بفواصل",
      },
      argName: {
        lang: "اللغة",
        request: "الطلب",
        discovery: "النوادي التي وُجدت",
        search: "النوادي المطلوب فحصها",
        max_clubs: "عدد النوادي",
        target: "اليوم المطلوب عرضه",
        url: "الصفحة المراد فتحها",
        urls: "الصفحات المراد فتحها",
        pages: "الصفحات المراد فتحها",
        script: "نص قراءة الصفحة البرمجي",
        script_args: "قيم لنص قراءة الصفحة",
        script_timeout_s: "المهلة لنص قراءة الصفحة (ثوانٍ)",
        steps: "ما يُفعل في الصفحة",
        code: "الشيفرة المراد تشغيلها",
        files: "ملفات الشيفرة",
        input: "قيم للشيفرة",
        stdin: "نص إدخال للشيفرة",
        output: "صيغة النتيجة",
        timeout_s: "المهلة (ثوانٍ)",
        memory_mb: "حد الذاكرة (ميغابايت)",
        invoice_id: "رقم الفاتورة",
        amount: "المبلغ",
        currency: "العملة",
        pay_on: "تاريخ الدفع",
        invoice: "الفاتورة",
        query: "البحث عن",
        question: "السؤال",
        status: "الحالة",
        date: "التاريخ",
        city: "المدينة",
        to: "الإرسال إلى",
        subject: "الموضوع",
        body: "التفاصيل",
        note: "ملاحظة",
        cc: "نسخة إلى",
        body_text: "نص الرسالة",
        destination: "إلى أين تُرسل",
        event_type: "نوع الحدث",
        payload: "ما يحمله",
        subject_key: "ما يوافق عليه الشخص",
      },
      outName: { steps: "الخطوات" },
      enumValue: { approve: "تمت الموافقة", reject: "مرفوض", edit: "عُدّل ثم وُوفق عليه", yes: "نعم", no: "لا", true: "نعم", false: "لا" },
      roleName: {
        "code-runner": "مشغّل الشيفرة",
        "browser-operator": "مشغّل المتصفح",
        "file-editor": "محرر الملفات",
        "directory-operator": "مشغّل الدليل",
      },
      ref: {
        fromStep: "من الخطوة ‹{{step}}› ← {{field}}",
        requestField: "من طلب المستخدم ← {{field}}",
        unknown: "{{field}}",
      },
      edge: {
        if: "إذا {{condition}}",
        clause: "{{subject}} {{op}} {{value}}",
        ok: "نجحت ‹{{step}}›",
        not_ok: "لم تنجح ‹{{step}}›",
        toolCall: "عندما يطلب الذكاء الاصطناعي {{tool}}: {{action}}",
        noToolCall: "عندما يصل الذكاء الاصطناعي إلى إجابته",
        condition: "شرط",
      },
      field: {
        label_en: "اسم الخطوة (بالإنجليزية)",
        label_ar: "اسم الخطوة (بالعربية)",
        id: "المعرّف التقني",
        promptRef: "موجّه محفوظ (اختياري)",
        template: "تعليمات للذكاء الاصطناعي",
        variables: "القيم المستخدمة في التعليمات",
        llmTools: "الأنظمة التي يمكن للذكاء الاصطناعي استخدامها هنا",
        tool: "النظام",
        action: "ما الذي يفعله",
        args: "ما يُرسَل",
        routes: "إلى أين بعد ذلك",
        default: "وإلا فانتقل إلى",
        subjectKey: "ما يوافق عليه الشخص",
        approverRoles: "من يوافق",
        sla: "القرار خلال",
        onReject: "عند الرفض انتقل إلى",
        question_en: "السؤال (بالإنجليزية)",
        question_ar: "السؤال (بالعربية)",
        replyKey: "احفظ الرد باسم",
        branches: "شغّل هذه معًا",
        join: "ثم تابع عند",
        strategy: "اجمع النتائج بطريقة",
        agent: "وكيل آخر",
        agentVersion: "الإصدار",
        collection: "المعرفة التي يبحث فيها",
        query: "ابحث عن",
        entrypoint: "الشيفرة التي تُشغَّل",
        when: "اسلك هذا الطريق عندما",
        outputKey: "احفظ النتيجة باسم",
        maxIterations: "التكرار بحد أقصى",
      },
      help: {
        id: "يُنشأ من الاسم الإنجليزي. أحرف إنجليزية صغيرة وأرقام و _؛ وتغييره يحدّث كل الروابط.",
        promptRef: "موجّه من سجل الموجّهات، مثل prompts:/invoice-system@production، ويحل محل التعليمات أعلاه.",
        template: "اذكر ما يجب أن تفعله هذه الخطوة. {name} تُدرج إحدى القيم أدناه.",
        variables: "قيم من خطوات سابقة تستخدمها التعليمات.",
        llmTools: "تظهر فقط الأنظمة المسموح لهذا الوكيل باستخدامها.",
        tool: "اختيار نظام هنا يسمح للوكيل باستخدامه أيضًا.",
        action: "يمكن وضع الإجراءات التي «تغيّر البيانات» خلف موافقة.",
        args: "املأ كل قيمة، أو خذها من خطوة سابقة.",
        routes: "تُفحص من الأعلى إلى الأسفل، ويُستخدم أول ما يتطابق.",
        default: "يُستخدم عندما لا يتطابق أيٌّ من المسارات أعلاه.",
        subjectKey: "القيمة التي يراها المعتمِد ويقرر بشأنها.",
        approverRoles: "يمكن لأي شخص يحمل أحد هذه الأدوار أن يقرر. ولا يمكن لصاحب الطلب أن يوافق.",
        sla: "بساعات العمل. في منتصف المدة يُرسل تذكير؛ وإن حدّدت لمن يُرسل بعد ذلك، ينتقل إليه عند انتهاء المدة.",
        onReject: "اتركه فارغًا لإيقاف التشغيل عند رفض الطلب.",
        replyKey: "اسم تستخدمه الخطوات اللاحقة لقراءة الرد.",
        query: "عادةً طلب المستخدم.",
        when: "اتركه فارغًا لسلوك هذا الطريق دائمًا.",
        outputKey: "اسم تستخدمه الخطوات اللاحقة لقراءة هذه النتيجة. الفارغ يعني «{{fallback}}».",
        maxIterations: "عدد مرات تشغيل هذه الخطوة في الطلب الواحد؛ يمنع الحلقات اللانهائية. عند بلوغه ينتقل التشغيل إلى ما يحدده «إذا بلغ الحد، انتقل إلى»، أو يتوقف برسالة واضحة ويحتفظ بآخر إجابة.",
      },
      nodeHelp: {
        llm: "يقرأ الذكاء الاصطناعي التعليمات والنتائج السابقة، ثم يجيب أو يطلب مساعدة نظام.",
        tool: "يستخدم نظامًا (مثل نظام تخطيط الموارد) للبحث عن شيء أو إجراء تغيير.",
        router: "يختار الخطوة التالية بناءً على النتائج السابقة.",
        human_approval: "ينتظر حتى يوافق شخص أو يرفض.",
        human_input: "يسأل المستخدم سؤالًا وينتظر الرد.",
        parallel: "يشغّل عدة خطوات في الوقت نفسه.",
        join: "ينتظر الخطوات المتوازية ويجمع نتائجها.",
        subgraph: "يسلّم العمل إلى وكيل آخر.",
        retrieval: "يبحث في مجموعة معرفة عن المستندات ذات الصلة.",
        python: "يشغّل شيفرة مراجَعة (غير متاحة للوكلاء المصنوعين في المنشئ الموجّه).",
        end: "ينتهي التشغيل هنا.",
      },
      role: {
        approver: "المعتمِدون",
        "finance-approver": "المعتمِدون الماليون",
        "finance-director": "المديرون الماليون",
        cfo: "المدير المالي التنفيذي",
        reviewer: "المراجِعون",
        "case-supervisor": "مشرفو الحالات",
        "privacy-officer": "مسؤولو الخصوصية",
        "release-approver": "معتمدو الإطلاق",
        "tenant-admin": "مسؤولو المؤسسة",
      },
      sla: {
        PT1H: "ساعة واحدة",
        PT4H: "4 ساعات",
        PT8H: "8 ساعات",
        P1D: "يوم واحد",
        P3D: "3 أيام",
      },
      issue: {
        toolNotAllowed: "الخطوة ‹{{step}}› تستخدم {{tool}} ({{action}})، لكن هذا الوكيل غير مسموح له باستخدامه بعد — اسمح به في «عن هذا الوكيل» › الأنظمة المسموحة.",
        promptBoth: "للخطوة ‹{{step}}› تعليمات خاصة بها وموجّه محفوظ معاً. أبقِ واحداً فقط: امسح التعليمات أو الموجّه المحفوظ.",
        promptNone: "لا توجد تعليمات للخطوة ‹{{step}}›. اكتب ما يجب أن يفعله الذكاء الاصطناعي، أو اختر موجّهاً محفوظاً.",
        parallelMixed: "الخطوة ‹{{step}}› تشغّل خطوات ذكاء اصطناعي أو برمجة في الوقت نفسه مع خطوات أنظمة أو أشخاص أو معرفة. لا يمكن تشغيلها معاً: ضعها في مجموعات منفصلة.",
        llmToolNotAllowed: "الخطوة ‹{{step}}› تعرض {{tool}} على الذكاء الاصطناعي، لكن هذا الوكيل غير مسموح له باستخدامه.",
        collectionNotDeclared: "الخطوة ‹{{step}}› تبحث في {{collection}}، وهي ليست ضمن معرفة هذا الوكيل بعد.",
        approvalNotAllowed: "قاعدة موافقة تشمل {{tool}} ({{action}})، وهذا الوكيل غير مسموح له باستخدامه.",
        approvalLevel_noRole: "الاعتماد قبل {{tool}} ({{action}}): المستوى {{level}} يحتاج إلى دور واحد على الأقل.",
        approvalLevel_noPerson: "الاعتماد قبل {{tool}} ({{action}}): المستوى {{level}} يحتاج إلى القيمة التي تحدد الشخص.",
        approvalLevel_quorumNotRoles: "الاعتماد قبل {{tool}} ({{action}}): يمكن طلب عدة أشخاص فقط في مستوى حسب الدور (المستوى {{level}}).",
        approvalLevel_tooMany: "الاعتماد قبل {{tool}} ({{action}}) فيه أكثر من ستة مستويات.",
        approvalStepLevel_noRole: "‘{{step}}’: المستوى {{level}} يحتاج إلى دور واحد على الأقل.",
        approvalStepLevel_noPerson: "‘{{step}}’: المستوى {{level}} يحتاج إلى القيمة التي تحدد الشخص.",
        approvalStepLevel_quorumNotRoles: "‘{{step}}’: يمكن طلب عدة أشخاص فقط في مستوى حسب الدور (المستوى {{level}}).",
        approvalStepLevel_tooMany: "‘{{step}}’ فيه أكثر من ستة مستويات اعتماد.",
        unreachable: "لا يمكن الوصول إلى هذه الخطوات من البداية أبدًا: {{steps}}. اربطها أو احذفها.",
        edgeUnknown: "رابط من ‹{{from}}› يشير إلى خطوة غير موجودة ({{to}}).",
        limitUnknown: "الخطوة ‹{{step}}› تنتقل إلى خطوة غير موجودة عند بلوغ حد التكرار. اختر خطوة أخرى في «إذا بلغ الحد، انتقل إلى».",
        limitSelf: "لا يمكن أن تعود الخطوة ‹{{step}}› إلى نفسها عند بلوغ حد التكرار. اختر خطوة أخرى.",
        limitNeedsRepeat: "الخطوة ‹{{step}}› تحدد وجهة عند بلوغ الحد لكن لا حد لها. اضبط «التكرار بحد أقصى» أو امسح «إذا بلغ الحد، انتقل إلى».",
        failUnknown: "الخطوة ‹{{step}}› تنتقل عند الفشل إلى خطوة غير موجودة. اختر خطوة أخرى في «إذا استمر الفشل، انتقل إلى».",
        failSelf: "لا يمكن أن تعود الخطوة ‹{{step}}› إلى نفسها عند الفشل. استخدم «أعد المحاولة» أو اختر خطوة أخرى.",
        waitNothing: "الخطوة ‹{{step}}› لا تنتظر شيئاً. اختر حدثاً أو موعداً أو إجابة شخص.",
        calcTwice: "الخطوة ‹{{step}}› تحسب {{values}} أكثر من مرة. أعطِ كل قيمة اسماً خاصاً بها.",
        loopList: "الخطوة ‹{{step}}› بلا قائمة بعد. اختر القائمة التي تمر عليها.",
        tableMatch: "الخطوة ‹{{step}}› تحتاج إلى الجدولين المراد مطابقتهما وعمود واحد على الأقل للمطابقة.",
        tableGroup: "الخطوة ‹{{step}}› تحتاج إلى الصفوف والأعمدة المراد التجميع حسبها، أو إجمالي.",
        loopBody: "الخطوة ‹{{step}}› لا تفعل شيئاً لكل عنصر بعد. أضف خطوة على سهم «لكل عنصر»، أو اختر وكيلاً آخر.",
        loopArrow: "السهم من ‹{{from}}› إلى ‹{{to}}› يخرج من الخطوات المنفّذة لكل عنصر في ‹{{step}}›. هذه الخطوات تؤدي إلى بعضها فقط؛ ويتابع التشغيل بعد ‹{{step}}› نفسها.",
        calcEmpty: "الخطوة ‹{{step}}›: القيمة ‹{{value}}› بلا معادلة بعد. اختر ما يجب حسابه.",
        calcFormula: "الخطوة ‹{{step}}›: معادلة ‹{{value}}› غير مكتملة. تحقق من الأقواس وعلامات الاقتباس.",
        waitEventName: "الخطوة ‹{{step}}› تنتظر حدثاً لكن لا اسم له.",
        waitEventKey: "الخطوة ‹{{step}}› تنتظر حدثاً لكنها لا تحدد أيّ حدث يخص هذا التشغيل. اختر القيمة التي يعرفها الطرفان، مثل رقم الطلب.",
        waitAt: "الخطوة ‹{{step}}› تنتظر تاريخاً ووقتاً لكن لم يُختر أيّ منهما.",
        waitGiveUpSelf: "لا يمكن أن تعود الخطوة ‹{{step}}› إلى نفسها إذا لم يصل شيء في الوقت. اختر خطوة أخرى.",
        entryMissing: "الخطوة الأولى ({{id}}) غير موجودة. اختر خطوة ثم «اجعلها عقدة البداية».",
        duplicateIds: "خطوتان لهما المعرّف التقني نفسه: {{ids}}.",
        badId: "المعرّف التقني ‹{{id}}› غير مسموح: استخدم أحرفًا إنجليزية صغيرة وأرقامًا و _.",
        agentName: "الاسم التقني للوكيل غير صالح.",
        agentVersion: "يجب أن يكون رقم النسخة بالشكل 1.0.0.",
        autonomous: "الوكلاء الذين يعملون بأنفسهم مقيدون بمستوى المخاطر 2.",
        nodeFieldMissing: "الخطوة ‹{{step}}› ما زالت تحتاج إلى {{field}}.",
        nodeFieldInvalid: "الخطوة ‹{{step}}› فيها قيمة غير صالحة لـ {{field}}.",
        nodeInvalid: "الخطوة ‹{{step}}› غير مكتملة بعد.",
        nodeLabel: "أعطِ الخطوة ‹{{step}}› اسمًا بـ{{lang}} أيضًا، أو اترك الاسمين فارغين.",
        agentText: "املأ اسم الوكيل ووصفه وهدفه باللغتين.",
        other: "فحص تقني: {{detail}}",
        dataLevel: "يحتوي {{tool}} بيانات {{level}}، لكن هذا الوكيل لا يتعامل إلا مع بيانات {{agentLevel}}، لذا ستُرفض طلباته. ارفع مستوى بيانات الوكيل إلى {{level}}.",
        riskTier: "تحتاج الخطوة ‹{{step}}› إلى مستوى الخطورة {{tier}}، لكن هذا الوكيل في المستوى {{agentTier}}، لذا ستُرفض.",
        riskTierNocode: "تحتاج الخطوة ‹{{step}}› إلى مستوى الخطورة {{tier}}، وهو أعلى من حد المنشئ الموجَّه (2). استخدم إجراءً آخر، أو اطلب من مطوّر بناء هذا الوكيل بالشيفرة.",
        roleNeeded: "تحتاج الخطوة ‹{{step}}› إلى دور {{roles}}، وهو ليس لديك. اطلبه من المسؤول، أو اجعل من لديه الدور يجرّب الوكيل.",
        modelLevel: "لا يجوز أن يرى {{model}} بيانات {{level}} (يرى حتى {{top}}). اختر نموذجًا آخر أو مستوى بيانات أقل.",
        modelLevelStep: "تستخدم الخطوة ‹{{step}}› النموذج {{model}}، ولا يجوز أن يرى بيانات {{level}} (حتى {{top}}). اختر نموذجًا آخر لهذه الخطوة.",
        modelMissing: "نموذج الذكاء الاصطناعي للوكيل ({{model}}) غير متاح في هذه المنصة، فلا يستطيع الإجابة. اختر نموذجًا آخر في «عن هذا الوكيل».",
        modelMissingStep: "تستخدم الخطوة ‹{{step}}› نموذج ذكاء اصطناعي ({{model}}) غير متاح في هذه المنصة. اختر نموذجًا آخر لهذه الخطوة.",
        collectionMissing: "المعرفة {{collection}} غير موجودة في مؤسستك، فالبحث فيها لا يجد شيئًا. اختر مجموعة موجودة، أو أضفها أولًا من «المعرفة».",
        promptMissing: "تستخدم الخطوة ‹{{step}}› التعليمات المحفوظة ‹{{prompt}}› وهي غير موجودة في مكتبة التعليمات. اختر تعليمات محفوظة أخرى، أو اكتب التعليمات في الخطوة.",
        unusedTeam: "يحتوي ملف هذا الوكيل على فريق وكلاء من نوع «مناظرة». هذا النوع من الفرق لم يعد يعمل: الخطوات في المخطط هي التي تحدد ما يحدث. احذفه لتجنّب الالتباس.",
        unusedCard: "يطلب ملف هذا الوكيل نشر بطاقة ليجده الوكلاء الآخرون. هذا غير متاح بعد، فلا يُنشر شيء.",
        teamStale: "لم تعد خطوات الفريق مطابقة للفريق (لا تسأل {{members}}). أعد بناء الخطوات من الفريق.",
        teamNoDescription: "عضو الفريق ‘{{member}}’ بلا وصف. اذكر ما يجيده، ليعرف الفريق متى يسأله.",
        cardNoSkills: "لا تذكر بطاقة الوكيل شيئًا مما يستطيع فعله، فلا يرى الوكلاء الآخرون إلا وصفه. أضف ما يستطيع فعله في «عن هذا الوكيل» › «المشاركة مع الوكلاء الآخرين».",
        unusedEscalate: "تحدد قاعدة موافقة لمن يُحال الطلب إذا لم يقرر أحد في الوقت المحدد. هذا غير متاح بعد: يبقى الطلب منتظرًا.",
        unusedFallback: "تم تحديد نموذج ذكاء اصطناعي احتياطي. لا يُستخدم: المنصة تختار البديل بنفسها عند تعطل نموذج.",
      },
      fieldName: {
        tool: "نظام",
        action: "ما الذي يفعله",
        collection: "المعرفة التي يبحث فيها",
        prompt: "تعليمات للذكاء الاصطناعي",
        subjectKey: "ما يوافق عليه الشخص",
        question: "السؤال",
        outputKey: "اسم للنتيجة",
        routes: "إلى أين بعد ذلك",
        default: "إلى أين وإلا",
        branches: "خطوات تعمل معًا",
        join: "أين يتابع",
        agent: "الوكيل الآخر",
        entrypoint: "الشيفرة التي تُشغَّل",
        args: "ما يُرسَل",
        maxIterations: "حد للتكرار",
      },
      fix: {
        grant: "اسمح به",
        knowledge: "أضِفها",
        dropLlmTool: "أزِله من هذه الخطوة",
        level: "ارفعه إلى {{level}}",
        tier: "ارفعه إلى المستوى {{tier}}",
        clear: "احذفه",
        rebuildTeam: "أعد بناء الخطوات",
      },
    },
    prompts: {
      newName: "اسم الموجّه الجديد",
      create: "موجّه جديد",
      createHelp: "أحرف لاتينية وأرقام ونقاط وشرطات وشرطات سفلية. يُنشأ الإصدار الأول عند الحفظ.",
      none: "لا توجد موجّهات في هذه الجهة بعد. أنشئ واحدًا للبدء.",
      nameInvalid: "استخدم أحرفًا لاتينية وأرقامًا ونقاطًا وشرطات (يبدأ بحرف أو رقم).",
      select: "الموجّه",
      pick: "اختيار الإصدار {{version}}",
      version: "الإصدار",
      aliases: "الأسماء المستعارة",
      message: "ملاحظة التغيير",
      created: "تاريخ الإنشاء",
      alias: "الاسم المستعار",
      setAlias: "توجيه الاسم المستعار إلى الإصدار {{version}}",
      aliasSet: "يشير {{alias}} الآن إلى الإصدار {{version}}",
      editor: "تحرير كإصدار جديد",
      template: "القالب",
      commit: "ملاحظة التغيير",
      save: "حفظ إصدار جديد",
      saved: "تم حفظ الإصدار الجديد",
    },
    runner: {
      select: "تشغيل مسجل",
      play: "إعادة التشغيل",
      pause: "إيقاف مؤقت",
      position: "الحدث {{pos}} من {{total}}",
      messages: "الرسائل",
      sources: "المصادر",
      activity: "النشاط",
      events: "أحداث AG-UI",
    },
    team: {
      title: "يعمل كفريق",
      intro: "يجيب الفريق بسؤال وكلاء آخرين في مؤسستك. يحتفظ كل عضو بقواعده واعتماداته وحدوده، ويظهر عمله داخل تشغيل الفريق.",
      switch: "يعمل هذا الوكيل كفريق من الوكلاء",
      replaceSteps: "ستحل خطوات الفريق محل الخطوات المرسومة الآن. هل تتابع؟",
      replaceOk: "استخدم فريقًا",
      replaceCancel: "أبقِ خطواتي",
      builtFromTeam: "تُبنى الخطوات من الفريق؛ لا حاجة لتعديلها يدويًا.",
      teamOfTeams: "يمكن أن يكون العضو نفسه فريقًا: هكذا تصنع فريقًا من الفرق.",
      hierarchical: "يذكر ملف هذا الوكيل «فرقًا تحت قائد»: يعمل كمشرف أعضاؤه فرق.",
      debate: "يحتوي ملف هذا الوكيل على فريق «مناظرة». هذا النوع من الفرق لم يعد يعمل.",
      debateRemove: "احذفه",
      kindQuestion: "كيف يعمل الأعضاء معًا؟",
      kind: {
        supervisor: { title: "مشرف", body: "يسأل المشرف الأعضاء ويجمع إجاباتهم." },
        handoff: { title: "تسليم", body: "يساعد عضو واحد في كل مرة، ويمكنه تسليم المحادثة لعضو آخر." },
      },
      add: "أضف عضوًا",
      addHelp: "وكلاء أُطلقوا في مؤسستك. يُسأل كل منهم فقط عن العمل الذي تصفه.",
      addPlaceholder: "اختر وكيلًا",
      noAgents: "لا يوجد وكلاء مُطلقون بعد",
      empty: "أضف عضوين على الأقل.",
      notReleased: "غير مُطلق",
      remove: "إزالة",
      removeAria: "أزل {{member}} من الفريق",
      role_en: "الاسم في الفريق (بالإنجليزية)",
      role_ar: "الاسم في الفريق (بالعربية)",
      description_en: "ما يجيده (بالإنجليزية)",
      description_ar: "ما يجيده (بالعربية)",
      descriptionHelp: "بكلمات بسيطة، مثل «الفواتير والمدفوعات والاسترداد». يقرأ الفريق هذا ليختار من يساعد.",
      handsTo: "يمكنه تسليم المحادثة إلى",
      handsToHelp: "عندما لا يستطيع هذا العضو المساعدة، يمكنه تحويل الشخص إلى هؤلاء الأعضاء (مع ذكر السبب).",
      handsToNobody: "لا أحد",
      technicalName: "الوكيل: {{name}}",
      limit: { supervisor: "أقصى عدد لسؤال المشرف للأعضاء في كل طلب", handoff: "أقصى عدد لتسليم المحادثة في كل طلب" },
      limitHelp: {
        supervisor: "بعدها تُجمع الإجابات التي وصلت، فلا يدور الطلب في حلقة.",
        handoff: "بعدها يحتفظ العضو الذي معه المحادثة بها.",
      },
      howItRuns: "يعمل الأعضاء بصلاحياتهم وفحوصاتهم واعتماداتهم؛ ويُتخطى العضو الذي لا يحق للشخص استخدامه ويُبلَّغ الفريق بالسبب.",
    },
    card: {
      title: "المشاركة مع الوكلاء الآخرين (بطاقة الوكيل)",
      intro: "تخبر بطاقة الوكيل الوكلاءَ والبرامج الأخرى في مؤسستك بما يستطيع هذا الوكيل فعله، ليتمكنوا من سؤاله (معيار A2A).",
      switch: "انشر بطاقة هذا الوكيل",
      skills: "ما يستطيع فعله",
      skillsHelp: "كل بند أمر واحد يمكن للوكلاء الآخرين طلبه منه.",
      skillName_en: "الاسم (بالإنجليزية)",
      skillName_ar: "الاسم (بالعربية)",
      skillDescription_en: "الوصف (بالإنجليزية)",
      skillDescription_ar: "الوصف (بالعربية)",
      tags: "كلمات مفتاحية",
      tagsHelp: "كلمات يبحث بها الوكلاء الآخرون.",
      addSkill: "أضف شيئًا يستطيع فعله",
      removeSkill: "إزالة",
      preview: "ما يقرؤه الوكلاء الآخرون",
      previewName: "الاسم",
      previewDescription: "الوصف",
      previewLevel: "البيانات التي يتعامل معها",
      previewLanguages: "اللغات",
      previewRisk: "مستوى المخاطر",
      languages: "العربية والإنجليزية",
      noSkills: "لم يُذكر شيء بعد: لا يرى الوكلاء الآخرون إلا الوصف.",
      unnamed: "(بلا اسم بعد)",
      where: "يقرأ الوكلاء الآخرون البطاقة هنا، بمفتاح واجهة برمجية للمؤسسة يذكر هذا الوكيل (القنوات › ربط التطبيقات):",
      callAt: "ويرسلون إليه الطلبات هنا:",
      released: "تعرض البطاقة الإصدار المُطلق في كل بيئة؛ المسودة ليس لها بطاقة.",
    },
    runStatus: { idle: "لم يبدأ", running: "قيد التشغيل", awaiting_approval: "بانتظار الاعتماد", finished: "مكتمل", error: "فشل", cancelled: "ملغى" },
    docs: {
      title: "الوكلاء كملفات",
      intro: "كل ما تبنيه هنا يُحفظ كملف وكيل واحد. يستطيع المطورون تنزيله (الخطوات › تنزيل كملف) والاحتفاظ به مع شيفرتهم وفحصه ورسمه بأداة سطر الأوامر أدناه، ثم تحميله هنا مجددًا.",
      cli: "أداة سطر الأوامر (ap)",
      cliHelp: "شغّل هذه الأوامر حيث تُثبَّت حزمة المطورين للمنصة. استبدل my-agent.yaml بملف وكيلك.",
      cmd: { validate: "افحص ملف الوكيل بحثًا عن أخطاء", render: "ارسم خطواته كصورة (بتسميات عربية)", init: "ابدأ ملف وكيل جديدًا", patterns: "اعرض الأشكال النموذجية" },
      patternsTitle: "أشكال نموذجية",
      patternsHelp: "افتح أحدها من الخطوات › أمثلة لتبدأ منه.",
    },
  },
};
